import { type FC, useState } from 'react';
import { Button, Space, Title } from '@g2u/ui-kit';
import { Dropdown, Table, type MenuProps } from 'antd';
import { TablePaginationConfig, type ColumnsType } from 'antd/es/table';
import { format, isValid, parseISO } from 'date-fns';

import { Customer, PrepaidItem, PrepaidProductStatus } from 'types';
import { CustomerSearchModal } from 'components';
import { EmailInvoiceSubmenu } from 'components/common/email-invoice-submenu';
import styles from './customer-prepaid-products-list.module.scss';

type PaginationMetadata = {
  total: number;
  current_page: number;
  pageSize: number;
  onChange: TablePaginationConfig['onChange'];
};

type Props = {
  isLoading?: boolean;
  items?: PrepaidItem[];
  paginationMetadata?: PaginationMetadata;
  onTransfer: (productId: number, customerId: number, name: string) => void;
  onStatusUpdate: (customerProductId: number, status: PrepaidProductStatus) => void;
  onViewComments?: (item: PrepaidItem) => void;
  onViewJobLogs?: (item: PrepaidItem) => void;
  onViewBillingHistory?: (item: PrepaidItem) => void;
  onSendTc?: (item: PrepaidItem) => void;
  onCancelSubscription?: (item: PrepaidItem) => void;
  onEmailInvoiceSave?: (subscriptionId: number, emailInvoice: 0 | 1) => void;
};

const DATE_FORMAT = 'dd MMM yyyy';
const defaultPageSize = 5;

const formatDate = (value: string | null): string => {
  if (!value) return '';
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, DATE_FORMAT) : value;
};

// The API embeds an ISO date inside pre-worded prose ("Started on 2026-05-01");
// reformat only the date substring, leaving the wording (and its casing) intact.
const formatDateInText = (text: string): string =>
  text.replace(/\d{4}-\d{2}-\d{2}/, (match) => {
    const parsed = parseISO(match);
    return isValid(parsed) ? format(parsed, DATE_FORMAT) : match;
  });

const composeStatus = (item: PrepaidItem): string => {
  // A Cancelled subscription is displayed as "Inactive" per the Figma.
  const label = item.display_status === 'Cancelled' ? 'Inactive' : item.display_status;

  if (item.type === 'subscription' && item.status_detail) {
    return `${label} (${formatDateInText(item.status_detail)})`;
  }

  return label;
};

const productMenuItems: MenuProps['items'] = [
  { label: 'Mark as cancelled', key: PrepaidProductStatus.Cancelled },
  { label: 'Mark as refunded', key: PrepaidProductStatus.Refunded },
];

type ProductActionsProps = {
  record: PrepaidItem;
  onStatusUpdate: (customerProductId: number, status: PrepaidProductStatus) => void;
  onTransferClick: (productId: number) => void;
};

// Product rows keep the existing Edit (status) + Transfer behaviour, gated on Active status.
const ProductActions: FC<ProductActionsProps> = ({ record, onStatusUpdate, onTransferClick }) => {
  const isActive = record.display_status === 'Active';

  return (
    <>
      <Dropdown
        menu={{
          items: productMenuItems,
          onClick: (selected) => onStatusUpdate(record.ID, selected.key as PrepaidProductStatus),
        }}
        trigger={['click']}
        arrow={{ pointAtCenter: true }}
        disabled={!isActive}
        placement="topRight"
      >
        <Button.$ type={Button.Type.Link} style={{ padding: 0 }} disabled={!isActive} onClick={() => null} dataTestId="edit-btn">
          Edit
        </Button.$>
      </Dropdown>
      <br />
      <Button.$
        dataTestId="transfer-product"
        type={Button.Type.Link}
        style={{ padding: 0 }}
        disabled={!isActive}
        onClick={() => onTransferClick(record.ID)}
      >
        Transfer
      </Button.$>
    </>
  );
};

type SubscriptionActionsProps = {
  record: PrepaidItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewComments?: (item: PrepaidItem) => void;
  onViewJobLogs?: (item: PrepaidItem) => void;
  onViewBillingHistory?: (item: PrepaidItem) => void;
  onSendTc?: (item: PrepaidItem) => void;
  onCancelSubscription?: (item: PrepaidItem) => void;
  onEmailInvoiceSave?: (subscriptionId: number, emailInvoice: 0 | 1) => void;
};

// Subscription rows get the lifecycle "Actions" dropdown. Open state is controlled by the
// parent so the email-invoice submenu can stay open while the user picks Yes/No + Save.
const SubscriptionActions: FC<SubscriptionActionsProps> = ({
  record,
  open,
  onOpenChange,
  onViewComments,
  onViewJobLogs,
  onViewBillingHistory,
  onSendTc,
  onCancelSubscription,
  onEmailInvoiceSave,
}) => {
  const isPending = record.display_status === 'Pending';
  const canCancel = record.display_status !== 'Inactive' && record.display_status !== 'Cancelled';

  const menuItems: MenuProps['items'] = [
    { key: 'comments', label: 'View comments' },
    { key: 'jobs', label: 'View job logs' },
    ...(canCancel
      ? [
          {
            key: 'email',
            label: 'Email Invoices',
            children: [
              {
                key: 'email-content',
                label: (
                  <EmailInvoiceSubmenu
                    subscriptionId={record.ID}
                    open={open}
                    onSave={(emailInvoice) => {
                      onEmailInvoiceSave?.(record.ID, emailInvoice);
                      onOpenChange(false);
                    }}
                  />
                ),
              },
            ],
          },
        ]
      : []),
    { key: 'billing', label: 'View billing history' },
    ...(isPending ? [{ key: 'tc', label: 'Send T&Cs' }] : []),
    ...(canCancel ? [{ key: 'cancel', label: 'Cancel subscription', danger: true }] : []),
  ];

  const onClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'comments':
        onViewComments?.(record);
        break;
      case 'jobs':
        onViewJobLogs?.(record);
        break;
      case 'billing':
        onViewBillingHistory?.(record);
        break;
      case 'tc':
        onSendTc?.(record);
        break;
      case 'cancel':
        onCancelSubscription?.(record);
        break;
    }
  };

  return (
    <Dropdown menu={{ items: menuItems, onClick }} trigger={['click']} placement="topRight" open={open} onOpenChange={onOpenChange}>
      <Button.$ type={Button.Type.Link} style={{ padding: 0 }} onClick={() => null} dataTestId="subscription-actions-btn">
        Actions
      </Button.$>
    </Dropdown>
  );
};

export const CustomerPrepaidProductsList: FC<Props> = (props) => {
  const {
    items,
    isLoading,
    paginationMetadata: meta,
    onTransfer,
    onStatusUpdate,
    onViewComments,
    onViewJobLogs,
    onViewBillingHistory,
    onSendTc,
    onCancelSubscription,
    onEmailInvoiceSave,
  } = props;

  const [selectedProduct, setSelectedProduct] = useState<number>();
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [openActionsKey, setOpenActionsKey] = useState<string | null>(null);

  const handleCustomerSelect = (productId: number) => {
    setSelectedProduct(productId);
    setIsCustomerModalOpen(true);
  };

  const handleTransfer = (customerId: number, name: string) => {
    if (selectedProduct) {
      onTransfer(selectedProduct, customerId, name);
      setIsCustomerModalOpen(false);
    }
  };

  const columnsDefinition: ColumnsType<PrepaidItem> = [
    {
      key: 'name',
      title: 'Product / Subscription Name',
      dataIndex: 'name',
    },
    {
      key: 'date',
      title: 'Purchase Date',
      render: (_: unknown, record: PrepaidItem) => formatDate(record.purchase_date),
    },
    {
      key: 'comments',
      title: 'Comments',
      render: (_: unknown, record: PrepaidItem) => record.comments || 'N/A',
    },
    {
      key: 'status',
      title: 'Status',
      // Rendered without the capitalize class so composed prose keeps its casing.
      render: (_: unknown, record: PrepaidItem) => <span>{composeStatus(record)}</span>,
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_: unknown, record: PrepaidItem) => {
        const rowKey = `subscription-${record.ID}`;

        return record.type === 'subscription' ? (
          <SubscriptionActions
            record={record}
            open={openActionsKey === rowKey}
            onOpenChange={(nextOpen) => setOpenActionsKey(nextOpen ? rowKey : null)}
            onViewComments={onViewComments}
            onViewJobLogs={onViewJobLogs}
            onViewBillingHistory={onViewBillingHistory}
            onSendTc={onSendTc}
            onCancelSubscription={onCancelSubscription}
            onEmailInvoiceSave={onEmailInvoiceSave}
          />
        ) : (
          <ProductActions record={record} onStatusUpdate={onStatusUpdate} onTransferClick={handleCustomerSelect} />
        );
      },
    },
  ];

  const paginationProps: TablePaginationConfig | false = meta
    ? {
        rootClassName: styles.customePrepaidProductsList__pagination,
        total: meta.total,
        current: meta.current_page,
        pageSize: meta.pageSize || defaultPageSize,
        showTotal: () => <>Total {meta.total} items</>,
        showSizeChanger: false,
        onChange: meta.onChange,
        hideOnSinglePage: false,
      }
    : false;

  return (
    <div className={styles.customePrepaidProductsList}>
      <Space.$ direction={Space.Direction.Horizontal} align={Space.Align.Center} justify={Space.Justify.SpaceBetween}>
        <Title.$ level={5} className={styles.customePrepaidProductsList__title}>
          Prepaid Products
        </Title.$>
      </Space.$>
      <br />
      <Table
        rowKey={(record) => `${record.type}-${record.ID}`}
        columns={columnsDefinition}
        dataSource={items}
        loading={isLoading}
        pagination={paginationProps}
      />
      <CustomerSearchModal
        customer={null}
        isOpen={isCustomerModalOpen}
        modalTitle={'Search Customer Files'}
        actions={[
          {
            title: 'Transfer',
            action: (record: Customer) => {
              handleTransfer(record.ID, record.name.full_name);
            },
          },
        ]}
        onOk={() => setIsCustomerModalOpen(false)}
        onCancel={() => setIsCustomerModalOpen(false)}
        emptyContent={() => null}
      />
    </div>
  );
};
